import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';

import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { fetchGraphQLApi } from '@helpers/callApi';
import { saveAuditLogAsset } from '@helpers/saveAsset';
import {
  AUDIT_APPLICATION,
  emitTdoAuditEvent,
} from '@helpers/emitTdoAuditEvent';
import { readBurnInLabel } from '@helpers/redactedFileStaleness';
import {
  AuditEventRecord,
  formatAuditEventsCsv,
} from '@common/utils/auditLogCsv';
import { TDOId } from '@common-modules/universal/models/Brands';
import { buildDocumentRedactionTasksAndRoutes } from '@common-modules/engines/ingestion/services';
import { MediaAssetRecord } from '@redact-modules/casemedia/mediaAssets';
import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails';
import {
  mergeRedactionStatusTagFields,
  type RedactionStatusTagWriteFields,
} from '@common-modules/mediaDetails/redactionStatuses';
import {
  lookupLatestDocumentCommentsSchemaId,
  lookupLatestTdoLockSchemaId,
} from '@cbsa-modules/universal/services';
import { formatTDOName } from '@utils';
import {
  DocumentComment,
  DocumentRedactionAssetV1,
  KeywordMetrics,
  OcrManifest,
  PiiEngineResult,
  PiiMetrics,
  TextSelectMetrics,
} from './models';
import { withoutLicenseMeterRecords } from './licenseMeter';
import {
  DOCUMENT_REDACTION_ASSET_TYPE,
  DOCUMENT_WATERMARK_IMAGE_ASSET_TYPE,
} from './store';
import { sanitizeStoredImageDataUrl } from './watermarkImage';
import {
  FETCH_DOCUMENT_COMMENTS_FAILURE,
  FETCH_DOCUMENT_COMMENTS_SUCCESS,
  UPDATE_DOCUMENT_COMMENT,
  UPDATE_DOCUMENT_COMMENT_FAILURE,
  UPDATE_DOCUMENT_COMMENT_SUCCESS,
} from './actions';
import type { GetActionCreatorPayloadT } from '@utils';

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Real PDF URL: queries the TDO for its PRIMARY "media" asset and returns the
// signedUri. Mirrors the pattern used by loadMediaData for the
// "redaction-media-data" asset.
//
// It must be `primaryAsset`, not the first of `assets(assetType: "media")`
// A TDO can hold several media assets — the Redact Document OCR
// engine republishes the searchable PDF with `setAsPrimary` — and only
// `primaryAsset` answers "which one IS this document". The previous query,
// `assets(orderBy: createdDateTime, limit: 1)`, answered a different question:
// "which media asset is newest" (the schema defaults `orderDirection` to
// `desc`). Those coincide today only because the last publisher also sets
// primary; an engine that writes a media asset WITHOUT setAsPrimary would make
// them diverge, and the newest-wins query would open the wrong file.
// ---------------------------------------------------------------------------
interface TdoTagRecord {
  value: string;
  redactionStatus?: string | null;
  /** Stable custom-status UUID; custom statuses only (VE-24546). */
  redactionStatusKey?: string | null;
}

interface MediaAssetQueryResponse {
  temporalDataObject: {
    name: string | null;
    details: {
      tags?: Array<TdoTagRecord | null> | null;
      // Vendor "Send to" request-ids stamped at ingestion (VE-23442). Present
      // only when the TDO was created via the matching integration.
      govQARequestId?: string | null;
      foiaXpressRequestId?: string | null;
      casepointRequestId?: string | null;
      exterroRequestId?: string | null;
      nuixRequestId?: string | null;
      // `veritoneFile.isOcrScanned` is set by the OCR engine once it has
      // republished the searchable PDF. It says OCR HAS run, not
      // that it succeeded on every page — the manifest carries per-page truth.
      veritoneFile?: {
        isOcrScanned?: boolean | null;
        ocrEngineId?: string | null;
      } | null;
    } | null;
    // Parent folder(s) the TDO is filed under. Used as the case context for
    // the generic external-integration send (VE-23442). We take the first
    // folder and treat it as the folder/case identity (mirrors the route's
    // `caseId` for the video flow). `folders` is a direct list on the TDO
    // (no `records` wrapper), matching mainPage/casemedia usage.
    folders: Array<{ id: string; name: string | null } | null> | null;
    primaryAsset: {
      id: string;
      signedUri: string;
      // Which engine produced this asset, if any. OCR stamps its own id on the
      // searchable copy it republishes; the uploaded original carries null.
      sourceData?: { engineId?: string | null } | null;
    } | null;
  } | null;
}

export interface TdoVendorRequestIds {
  govQa: string | null;
  foiaXpress: string | null;
  casepoint: string | null;
  exterro: string | null;
  nuix: string | null;
}

export interface TdoMediaInfo {
  /** TDO's user-visible filename (e.g. "court case 123.pdf"). */
  name: string | null;
  /** Signed URL for the source PDF. */
  mediaUri: string | null;
  /**
   * Id of the `media` asset `mediaUri` points at. The session pins
   * this for its whole life: the OCR engine republishes the searchable PDF as a
   * NEW primary `media` asset mid-session, and re-resolving "the current
   * primary" — to re-sign an expired URL, say — would silently swap the file
   * under work already in progress. Re-signing must resolve THIS id; picking up
   * the new file is the refresh prompt's job, on the user's say-so.
   */
  mediaAssetId: string | null;
  /**
   * The engine that produced the asset `mediaAssetId` names, or null for the
   * uploaded original.
   *
   * Read for ONE question: is the file this session is about to open OCR's
   * own output? A settled OCR task only means the document on screen is
   * searchable if the answer is yes — otherwise OCR's copy is a different file
   * and the reviewer has to be offered the reload rather than told search
   * works. Task status alone cannot answer it, because OCR can finish in the
   * window between resolving this asset and the watch's first reading.
   */
  mediaAssetEngineId: string | null;
  /**
   * Raw `details.tags` array from the TDO. The doc editor reads the
   * `IN_REDACTION_TAG_KEY` entry to seed `session.redactionStatus`; the
   * surrounding tag list is preserved so the status-update mutation can
   * write back a merged tags array without clobbering other tags.
   */
  tags: Array<TdoTagRecord | null>;
  /** Vendor "Send to" request-ids read from `details` (VE-23442). */
  vendorRequestIds: TdoVendorRequestIds;
  /** Parent folder (case) id, or null when the TDO has no folder. */
  folderId: string | null;
  /** Parent folder (case) name, or null. */
  folderName: string | null;
  /**
   * The OCR engine has republished this document as a searchable PDF,
   * read from `details.veritoneFile.isOcrScanned`. True means OCR
   * ran to completion, NOT that every page could be read — a page can fail on
   * its own and the flag is still set. Per-page truth lives in the
   * `redact-document-ocr-manifest` asset.
   *
   * Do not use this to decide whether to WAIT for OCR: it is only set at the
   * end, so it cannot distinguish "OCR is still running" from "OCR never ran
   * for this document". Task status answers that.
   */
  isOcrScanned: boolean;
  /** Engine id that produced the searchable PDF, or null. */
  ocrEngineId: string | null;
}

export async function serviceFetchTdoMediaInfo(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<TdoMediaInfo> {
  const query = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        name
        details
        folders {
          id
          name
        }
        primaryAsset(assetType: "media") {
          id
          signedUri
          sourceData {
            engineId
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<MediaAssetQueryResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `media asset fetch failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  // A null TDO with an empty errors array is NOT success: openDocument marks
  // the TDO read-verified for the statusHistory funnel on this function
  // returning (SECURITY-11), so "no errors" alone must never stand in for
  // "the server returned this TDO to this caller".
  if (!result.data?.temporalDataObject) {
    throw new Error('media asset fetch failed: TDO not returned');
  }
  const tdo = result.data?.temporalDataObject;
  const details = tdo?.details ?? null;
  const folder = tdo?.folders?.find((f) => !!f) ?? null;
  return {
    name: tdo?.name ?? null,
    mediaUri: tdo?.primaryAsset?.signedUri ?? null,
    mediaAssetId: tdo?.primaryAsset?.id ?? null,
    mediaAssetEngineId: tdo?.primaryAsset?.sourceData?.engineId ?? null,
    tags: Array.isArray(details?.tags) ? details.tags : [],
    vendorRequestIds: {
      govQa: details?.govQARequestId ?? null,
      foiaXpress: details?.foiaXpressRequestId ?? null,
      casepoint: details?.casepointRequestId ?? null,
      exterro: details?.exterroRequestId ?? null,
      nuix: details?.nuixRequestId ?? null,
    },
    folderId: folder?.id ?? null,
    folderName: folder?.name ?? null,
    isOcrScanned: !!details?.veritoneFile?.isOcrScanned,
    ocrEngineId: details?.veritoneFile?.ocrEngineId ?? null,
  };
}

/**
 * Id of the TDO's current primary `media` asset, or null when the TDO has none.
 * Used to tell whether OCR published a new file (VE-29505).
 *
 * Null means ONLY "no primary media asset". A TDO the server declined to return
 * throws, as in `serviceFetchTdoMediaInfo` above — the two are unrelated facts
 * and a caller reading one as the other would treat a denial as an empty TDO.
 */
export async function serviceFetchPrimaryMediaAssetId(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string | null> {
  const query = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        primaryAsset(assetType: "media") {
          id
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject: { primaryAsset: { id: string } | null } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `primary media asset fetch failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  // A null TDO with an empty errors array is how an authorization or visibility
  // failure arrives, not an answer about assets — see the same guard on
  // `serviceFetchTdoMediaInfo` (SECURITY-11). `ocrPublishedNewFile` catches this
  // and still offers the reload, so the throw costs the caller nothing and keeps
  // the null return meaning one thing.
  if (!result.data?.temporalDataObject) {
    throw new Error('primary media asset fetch failed: TDO not returned');
  }
  return result.data.temporalDataObject.primaryAsset?.id ?? null;
}

// ---------------------------------------------------------------------------
// PII detection re-run (VE-21915)
// ---------------------------------------------------------------------------

interface CreatePiiJobResponse {
  createJob: {
    id: string;
    targetId: string;
    status: string;
    // The job's OWN account of which task belongs to which engine, which is
    // the whole reason these mutations ask for more than an id — see
    // `CreatedJob`.
    tasks?: {
      records?: Array<{ id: string; engineId: string | null }> | null;
    } | null;
  };
}

/**
 * A job that was just created, and the tasks it was created WITH.
 *
 * The task ids matter as much as the job id. Without them the poller knew only
 * that a run had started, so it re-ran full discovery — paging the TDO's whole
 * task list, 100 rows at a time — every tick until the new task became
 * queryable, which is the one window the discover-once design was built to
 * avoid paying in. The create response already knows the answer; asking for it
 * costs nothing and turns those minutes back into `task(id)` reads.
 *
 * Keyed by engine so the caller can tell OCR's task from PII's in a chained
 * job. Empty when the API returned no task records — callers must treat a
 * missing id as "find it the old way" rather than as "there is no task".
 */
export interface CreatedJob {
  jobId: string;
  taskIdByEngineId: Readonly<Record<string, string>>;
}

const taskIdsByEngine = (
  records: Array<{ id: string; engineId: string | null }> | null | undefined
): Record<string, string> => {
  const byEngine: Record<string, string> = {};
  for (const record of records ?? []) {
    // First wins: a chain has one task per engine, and a malformed response
    // naming one twice must not silently pick the later row.
    if (record.engineId && record.id && !byEngine[record.engineId]) {
      byEngine[record.engineId] = record.id;
    }
  }
  return byEngine;
};

/** The task records both create mutations select. */
const CREATED_JOB_FIELDS = `
        id
        targetId
        status
        tasks {
          records {
            id
            engineId
          }
        }
`;

/**
 * Create a job pointed directly at the document PII detection engine for a
 * document that was already ingested (PDF converter ran at upload, PII did
 * not). Mirrors the video "DETECT" re-run: feed the engine the already-
 * converted PDF's signed URL instead of re-running the full DAG.
 *
 * Returns the new job id and the task it was created with — see `CreatedJob`.
 */
export async function serviceCreatePiiDetectionJob(
  tdoId: TDOId,
  pdfUrl: string,
  piiEngineId: string,
  clusterId: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<CreatedJob> {
  const input = {
    targetId: tdoId,
    clusterId: clusterId || '',
    tasks: [
      {
        engineId: piiEngineId,
        payload: { url: pdfUrl },
      },
    ],
  };
  const query = `
    mutation($input: CreateJob!) {
      createJob(input: $input) {${CREATED_JOB_FIELDS}      }
    }
  `;
  const result = await fetchGraphQLApi<CreatePiiJobResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { input },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `PII detection job creation failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const jobId = result.data?.createJob?.id;
  if (!jobId) {
    throw new Error('PII detection job creation returned no job id');
  }
  return {
    jobId,
    taskIdByEngineId: taskIdsByEngine(result.data?.createJob?.tasks?.records),
  };
}

/**
 * Start OCR — and optionally PII behind it — on the document already open.
 *
 * The SAME DAG ingestion builds for a PDF upload, via the same builder: there
 * is one definition of how these engines chain, and a second one here would
 * drift from it the first time the chain changed. `skipPdfConverter` because
 * this document is already a PDF on the TDO — that is what the editor opened.
 *
 * NO PAYLOAD URL ON THE ROOT TASK, deliberately. The builder leaves it off so
 * the engine resolves `primaryAsset(assetType: "media")` at run time, which is
 * exactly the file the reviewer is looking at, and is the path ingestion
 * already proves for PDF uploads. Handing it `session.pdfUrl` instead would
 * pin the job to a signed URL that expires — and we do not know this engine's
 * payload key for one, where the converter takes `fileUrl` and PII takes `url`.
 *
 * `runPii` chains detection behind OCR in one job rather than two, so PII reads
 * the searchable copy OCR republishes rather than the file as it is now.
 */
export async function serviceCreateDocumentCognitionJob(
  tdoId: TDOId,
  engines: {
    ocrEngineId: string;
    piiEngineId?: string | null;
  },
  runPii: boolean,
  clusterId: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<CreatedJob> {
  const [tasks, routes] = buildDocumentRedactionTasksAndRoutes({
    // `pdfConverterEngineId` and `fileUrl` are OMITTED, not blanked:
    // `skipPdfConverter` keeps the converter out of the chain, and OCR at the
    // root carries no payload. They used to be passed as `''` with a comment
    // saying nothing reads them — which types as a valid engine id and reads
    // identically to a config value that came back empty.
    documentOcrEngineId: engines.ocrEngineId,
    documentPIIDetectionEngineId: runPii
      ? (engines.piiEngineId ?? undefined)
      : undefined,
    runOCR: true,
    runPII: runPii,
    skipPdfConverter: true,
  });
  if (!tasks.length) {
    throw new Error('No OCR engine configured');
  }

  const input = {
    targetId: tdoId,
    clusterId: clusterId || '',
    tasks,
    ...(routes.length ? { routes } : {}),
  };
  const query = `
    mutation($input: CreateJob!) {
      createJob(input: $input) {${CREATED_JOB_FIELDS}      }
    }
  `;
  const result = await fetchGraphQLApi<CreatePiiJobResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { input },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `OCR job creation failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  const jobId = result.data?.createJob?.id;
  if (!jobId) {
    throw new Error('OCR job creation returned no job id');
  }
  return {
    jobId,
    taskIdByEngineId: taskIdsByEngine(result.data?.createJob?.tasks?.records),
  };
}

/**
 * Every `media` asset on this TDO, for callers that must tell the file as it
 * was ingested from the searchable copy OCR republished.
 *
 * Returns the raw records; `findPdfOriginalMediaAsset` (casemedia/mediaAssets)
 * owns the rule for which is which, so export and the folder-view downloads
 * cannot disagree about what "the original" means.
 */
export async function serviceFetchDocumentMediaAssets(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<MediaAssetRecord[]> {
  const query = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "media", orderBy: createdDateTime, orderDirection: desc, limit: 50) {
          records {
            id
            name
            contentType
            createdDateTime
            signedUri
            details
            sourceData { engineId }
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject: {
      assets?: { records?: MediaAssetRecord[] | null } | null;
    } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `media asset query failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  return result.data?.temporalDataObject?.assets?.records ?? [];
}

// Task statuses that mean "no longer doing work". Anything else (pending,
// queued, running, accepted, waiting, …) counts as in-flight.
const TERMINAL_TASK_STATUSES = new Set([
  'complete',
  'failed',
  'cancelled',
  'aborted',
]);

export interface EngineTaskStatus {
  /** A task for the engine is still in flight (not in a terminal state). */
  hasRunning: boolean;
  /** A task for the engine has completed, or its result asset already exists. */
  hasComplete: boolean;
  /** A task for the engine reached a terminal state other than `complete`. */
  hasFailed: boolean;
  /**
   * The task is still not finished 12h after it was created — running,
   * queued, or never picked up. Reported separately from `hasRunning` so the
   * UI can stop claiming progress it has no evidence of.
   */
  hasStalled: boolean;
  /**
   * Nothing found for this engine YET, and we have not finished looking.
   *
   * Discovery reads the newest tasks; a job that has been created but whose
   * task row has not committed shows nothing, which is not the same answer as
   * "this engine never ran". Callers keep waiting while this holds and give up
   * on their own clock (`MAX_POLLS_AWAITING_*`) rather than asserting either.
   */
  mayStillAppear: boolean;
}

/** What discovery found for one engine. */
export interface EngineWork {
  /**
   * The engine's OUTPUT is already on the TDO, so its task status is moot.
   *
   * The cheap path: a document opened long after ingestion needs no task poll
   * at all. It assumes the newest result is the current one — i.e. that no
   * RE-RUN is in flight behind an older asset. That holds while nothing in the
   * app re-runs these engines (PII's re-run goes through its own action, which
   * restarts the watch and is handled by the caller).
   *
   * IF REPROCESSING IS EVER ADDED: delete the previous result assets as part of
   * it, or this short-circuit will report the old run's output as the new run's
   * and never poll the new task.
   */
  resultExists: boolean;
  /** Newest task for this engine, or null if discovery found none. */
  taskId: string | null;
  /** That task's status, or null when there is no task. */
  status: string | null;
  /**
   * When the task was created, for the stalled-run check.
   *
   * Creation, not start: a task can sit queued for a cluster indefinitely and
   * never start at all, which is precisely one of the ways a run is lost. The
   * cost is that a PII task chained behind a long OCR run is measured from when
   * the JOB was submitted — deliberate, since a document still unprocessed a
   * day after the reviewer asked is stalled whichever stage is holding it.
   */
  createdDateTime: string | null;
}

/**
 * How long a task may sit un-finished before the UI calls it lost.
 *
 * Not a cancellation — nothing is stopped, and a task that finishes later still
 * reports normally. It only decides when the reviewer is told to stop waiting
 * and offered the run again.
 *
 * Twelve hours, halved from a day: a reviewer who opens a document in the
 * morning should not be told a run from the previous afternoon is still on its
 * way. The cost of being wrong is small in both directions — too eager offers a
 * re-run for a job that was going to finish, too patient leaves a spinner up —
 * so this is tuned for the working day rather than for certainty.
 */
export const TASK_STALL_MS = 12 * 60 * 60 * 1000;

/**
 * Parse a server timestamp to epoch milliseconds, or NaN.
 *
 * `Date.parse` on an ISO-8601 string that CARRIES a zone designator is already
 * UTC-normalised on both sides, so the subtraction below is timezone-correct as
 * it stands. The hazard is the string that does NOT carry one: ES2015 says a
 * bare date-time form is local time, so `2026-09-24T09:00:00` from a UTC server
 * reads as 09:00 in whatever zone the reviewer's machine is set to — an error
 * of up to fourteen hours, which at a twelve-hour threshold is the difference
 * between "running" and "lost". aiWARE stamps the zone today; this makes the
 * parse independent of that continuing to be true.
 *
 * WHAT THIS DOES NOT FIX, because no amount of timezone handling can: the
 * comparison is still a SERVER timestamp against the BROWSER's clock, so a
 * machine whose clock is wrong measures the age wrong. Halving the threshold
 * halves the tolerance for that. Left as a known residual rather than plumbed
 * around, because a client that far out of step fails token validation long
 * before it reaches this code.
 */
export function parseServerTimestamp(value: string | null): number {
  if (!value) {
    return NaN;
  }
  const trimmed = value.trim();
  // Zone designator: trailing Z, or ±HH:MM / ±HHMM after the time part.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  return Date.parse(hasZone ? trimmed : `${trimmed}Z`);
}

export interface EngineDiscovery {
  ocr: EngineWork;
  pii: EngineWork;
}

const NO_WORK: EngineWork = {
  resultExists: false,
  taskId: null,
  status: null,
  createdDateTime: null,
};

/**
 * One engine's situation, as the watch's classifiers read it.
 *
 * `stillLooking` is the caller's, not the data's: discovery cannot distinguish
 * "no task yet" from "no task ever", so whoever is still willing to look says
 * so and the classifier keeps its counsel.
 */
export function engineStatusFromWork(
  work: EngineWork,
  stillLooking: boolean,
  now: number = Date.now()
): EngineTaskStatus {
  const status = (work.status ?? '').toLowerCase();
  // A task we can see outranks a result asset: on a re-run both exist, and the
  // asset is the PREVIOUS run's. The asset only answers where no task was found.
  if (!status && work.resultExists) {
    return {
      hasRunning: false,
      hasComplete: true,
      hasFailed: false,
      hasStalled: false,
      mayStillAppear: false,
    };
  }
  if (!status) {
    return {
      hasRunning: false,
      hasComplete: false,
      hasFailed: false,
      hasStalled: false,
      mayStillAppear: stillLooking,
    };
  }
  const terminal = TERMINAL_TASK_STATUSES.has(status);
  const created = parseServerTimestamp(work.createdDateTime);
  // An unparseable or absent timestamp is NOT a stall: it is no evidence
  // either way, and calling a healthy run lost is worse than waiting.
  const stalled =
    !terminal && Number.isFinite(created) && now - created > TASK_STALL_MS;
  return {
    hasRunning: !terminal && !stalled,
    hasComplete: status === 'complete',
    // Only a terminal non-complete status counts as failed, so a queued task is
    // never reported as a failure.
    hasFailed: terminal && status !== 'complete',
    hasStalled: stalled,
    mayStillAppear: false,
  };
}

interface DiscoveryResponse {
  temporalDataObject: {
    ocrManifestAssets?: {
      records: Array<{
        sourceData?: { engineId?: string | null } | null;
      }> | null;
    } | null;
    piiResultAssets?: {
      records: Array<{
        sourceData?: { engineId?: string | null } | null;
      }> | null;
    } | null;
    tasks: {
      records: Array<{
        id: string;
        status: string | null;
        engineId: string | null;
        createdDateTime?: string | null;
      }> | null;
    } | null;
  } | null;
}

/** Tasks are read newest-first so the run in progress is on the first page. */
const DISCOVERY_PAGE = 100;
/** Stop rather than walk a TDO with thousands of tasks looking for two ids. */
const MAX_DISCOVERY_PAGES = 10;

// A function, not a const: `OCR_MANIFEST_ASSET_TYPE` is declared further down
// and a module-level template literal would read it inside its TDZ.
const resultsQuery = () => `
  query($id: ID!) {
    temporalDataObject(id: $id) {
      ocrManifestAssets: assets(assetType: "${OCR_MANIFEST_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 5) {
        records { sourceData { engineId } }
      }
      piiResultAssets: assets(assetType: "vtn-standard", orderBy: createdDateTime, orderDirection: desc, limit: 100) {
        records { sourceData { engineId } }
      }
    }
  }
`;

const TASKS_QUERY = `
  query($id: ID!, $limit: Int!, $offset: Int!) {
    temporalDataObject(id: $id) {
      tasks(limit: $limit, offset: $offset, orderBy: [{ field: createdDateTime, direction: desc }]) {
        records { id status engineId createdDateTime }
      }
    }
  }
`;

const producedBy = (
  records:
    | Array<{ sourceData?: { engineId?: string | null } | null }>
    | null
    | undefined,
  engineId: string | null
) =>
  !!engineId &&
  (records ?? []).some((a) => a.sourceData?.engineId === engineId);

/**
 * Find, once per open, what each engine has already produced and which task to
 * watch — the expensive lookup, paid on open instead of every tick.
 *
 * RESULTS FIRST, TASKS ONLY IF NEEDED. A document opened after its ingestion
 * finished has both result assets on the TDO, and that is the answer: the task
 * list is never queried at all. Only an engine with no result costs a task
 * lookup, and that lookup mirrors the media editor's `fetchTdo` — a flat,
 * paginated task list on the TDO — except it stops as soon as every outstanding
 * engine is accounted for, since the newest tasks are the run in progress.
 *
 * `checkResults` is how a RE-RUN opts out: its engine's old asset is still on
 * the TDO and would otherwise report the previous run's output as this one's.
 * See `EngineWork.resultExists` for what re-processing must delete.
 */
export async function serviceDiscoverEngineWork(
  tdoId: TDOId,
  engines: { ocrEngineId: string | null; piiEngineId: string | null },
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string,
  checkResults: { ocr: boolean; pii: boolean } = { ocr: true, pii: true }
): Promise<EngineDiscovery> {
  const endpoint = `${apiRoot}/${graphQLEndpoint}`;
  const ocr: EngineWork = { ...NO_WORK };
  const pii: EngineWork = { ...NO_WORK };

  if (
    (engines.ocrEngineId && checkResults.ocr) ||
    (engines.piiEngineId && checkResults.pii)
  ) {
    const result = await fetchGraphQLApi<DiscoveryResponse>({
      endpoint,
      query: resultsQuery(),
      variables: { id: tdoId },
      token,
      veritoneAppId,
    });
    if (result.errors?.length) {
      throw new Error(
        `engine result lookup failed: ${
          result.errors[0].message ?? 'unknown error'
        }`
      );
    }
    const tdo = result.data?.temporalDataObject;
    ocr.resultExists =
      checkResults.ocr &&
      producedBy(tdo?.ocrManifestAssets?.records, engines.ocrEngineId);
    pii.resultExists =
      checkResults.pii &&
      producedBy(tdo?.piiResultAssets?.records, engines.piiEngineId);
  }

  const ocrOutstanding = !!engines.ocrEngineId && !ocr.resultExists;
  const piiOutstanding = !!engines.piiEngineId && !pii.resultExists;
  if (!ocrOutstanding && !piiOutstanding) {
    // The whole point: nothing to watch, so the task list is never read.
    return { ocr, pii };
  }

  for (let page = 0; page < MAX_DISCOVERY_PAGES; page++) {
    const result = await fetchGraphQLApi<DiscoveryResponse>({
      endpoint,
      query: TASKS_QUERY,
      variables: {
        id: tdoId,
        limit: DISCOVERY_PAGE,
        offset: page * DISCOVERY_PAGE,
      },
      token,
      veritoneAppId,
    });
    if (result.errors?.length) {
      throw new Error(
        `engine task lookup failed: ${
          result.errors[0].message ?? 'unknown error'
        }`
      );
    }
    const rows = result.data?.temporalDataObject?.tasks?.records ?? [];
    for (const row of rows) {
      // Newest first, so the first row seen for an engine is its current task.
      if (
        ocrOutstanding &&
        row.engineId === engines.ocrEngineId &&
        !ocr.taskId
      ) {
        ocr.taskId = row.id;
        ocr.status = row.status ?? null;
        ocr.createdDateTime = row.createdDateTime ?? null;
      }
      if (
        piiOutstanding &&
        row.engineId === engines.piiEngineId &&
        !pii.taskId
      ) {
        pii.taskId = row.id;
        pii.status = row.status ?? null;
        pii.createdDateTime = row.createdDateTime ?? null;
      }
    }
    const done =
      (!ocrOutstanding || !!ocr.taskId) && (!piiOutstanding || !!pii.taskId);
    if (done || rows.length < DISCOVERY_PAGE) {
      break;
    }
  }

  return { ocr, pii };
}

interface TaskStatusesResponse {
  ocr?: { id: string; status: string | null } | null;
  pii?: { id: string; status: string | null } | null;
}

/**
 * The steady-state poll: the status of the one or two tasks being watched.
 *
 * Two id lookups, where this used to read every task on the TDO plus its jobs
 * and their task lists on every tick. Absent ids are left out of the query
 * rather than sent as null, so an engine with no task costs nothing.
 */
export async function serviceFetchTaskStatuses(
  taskIds: { ocr?: string | null; pii?: string | null },
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{ ocr: string | null; pii: string | null }> {
  const wanted = [
    taskIds.ocr ? { alias: 'ocr', id: taskIds.ocr } : null,
    taskIds.pii ? { alias: 'pii', id: taskIds.pii } : null,
  ].filter((w): w is { alias: string; id: string } => !!w);
  if (!wanted.length) {
    return { ocr: null, pii: null };
  }

  const query = `
    query(${wanted.map((w) => `$${w.alias}: ID!`).join(', ')}) {
      ${wanted
        .map((w) => `${w.alias}: task(id: $${w.alias}) { id status }`)
        .join('\n      ')}
    }
  `;
  const variables = Object.fromEntries(wanted.map((w) => [w.alias, w.id]));

  const result = await fetchGraphQLApi<TaskStatusesResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables,
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `task status query failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  return {
    ocr: result.data?.ocr?.status ?? null,
    pii: result.data?.pii?.status ?? null,
  };
}

interface PiiResultAssetResponse {
  temporalDataObject: {
    assets: {
      records: Array<{
        id: string;
        signedUri: string | null;
        createdDateTime: string;
        sourceData: { engineId: string | null } | null;
      }> | null;
    } | null;
  } | null;
}

/**
 * Find the newest `vtn-standard` result asset whose `sourceData.engineId`
 * matches the document PII detection engine (VE-21915). Returns its signed
 * URL, or null if no matching asset exists yet. Mirrors the video detection
 * flow's `fetchDetectionResults` (filter vtn-standard assets by engine id).
 */
export async function serviceFindPiiResultAssetUrl(
  tdoId: TDOId,
  piiEngineId: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string | null> {
  const query = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "vtn-standard", orderBy: createdDateTime, limit: 100) {
          records {
            id
            signedUri
            createdDateTime
            sourceData {
              engineId
            }
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<PiiResultAssetResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `PII result asset query failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const records = result.data?.temporalDataObject?.assets?.records ?? [];
  const matches = records
    .filter((a) => a.sourceData?.engineId === piiEngineId && a.signedUri)
    // Newest first — `orderBy` direction isn't guaranteed, so sort explicitly.
    .sort((a, b) => b.createdDateTime.localeCompare(a.createdDateTime));
  return matches[0]?.signedUri ?? null;
}

/**
 * Download + parse the PII engine result JSON from its signed URL. The asset
 * is plain JSON (vtn-standard / aion schema), so a direct fetch is enough.
 */
export async function serviceDownloadPiiResult(
  signedUri: string
): Promise<PiiEngineResult> {
  const res = await fetch(signedUri);
  if (!res.ok) {
    throw new Error(`PII result download failed: HTTP ${res.status}`);
  }
  return (await res.json()) as PiiEngineResult;
}

// ---------------------------------------------------------------------------
// OCR manifest asset
//
// The `redact-document-ocr-manifest` asset published beside the searchable PDF.
// Found by assetType and narrowed by `sourceData.engineId`, the same way the
// PII result asset is found above.
// ---------------------------------------------------------------------------

/** assetType the OCR engine publishes its manifest under. */
export const OCR_MANIFEST_ASSET_TYPE = 'redact-document-ocr-manifest';

interface OcrManifestAssetResponse {
  temporalDataObject: {
    assets: {
      records: Array<{
        id: string;
        signedUri: string | null;
        createdDateTime: string;
        sourceData: { engineId: string | null } | null;
      }> | null;
    } | null;
  } | null;
}

/**
 * Find the newest OCR manifest asset written by `ocrEngineId`. Returns its
 * signed URL, or null when the document has none — which is the normal case for
 * anything ingested before the OCR engine existed, not an error.
 */
export async function serviceFindOcrManifestUrl(
  tdoId: TDOId,
  ocrEngineId: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string | null> {
  const query = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${OCR_MANIFEST_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 20) {
          records {
            id
            signedUri
            createdDateTime
            sourceData {
              engineId
            }
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<OcrManifestAssetResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `OCR manifest asset query failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const records = result.data?.temporalDataObject?.assets?.records ?? [];
  const matches = records
    .filter((a) => a.sourceData?.engineId === ocrEngineId && a.signedUri)
    // Newest first — `orderDirection` is asked for but not relied on, matching
    // serviceFindPiiResultAssetUrl.
    .sort((a, b) => b.createdDateTime.localeCompare(a.createdDateTime));
  return matches[0]?.signedUri ?? null;
}

/**
 * Download and validate the OCR manifest.
 *
 * Returns null rather than throwing for a manifest that is present but not
 * usable — no `pages` array, or a `pageCount` that disagrees with the PDF it is
 * supposed to describe. Either means the document must fall back to "no OCR
 * information": reporting per-page provenance from a manifest that has drifted
 * from its file would put an "OCR read this page" badge on a page the engine
 * never touched.
 *
 * `assetFormatVersion` is deliberately NOT gated. The field is still read off
 * the wire and kept on the parsed manifest for diagnostics.
 *
 * `pdfPageCount` is optional because the editor learns the real count from
 * react-pdf after the manifest loads; pass it when known and the cross-check
 * runs, omit it and only the version gate applies.
 */
export async function serviceDownloadOcrManifest(
  signedUri: string,
  pdfPageCount?: number
): Promise<OcrManifest | null> {
  const res = await fetch(signedUri);
  if (!res.ok) {
    throw new Error(`OCR manifest download failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as Partial<OcrManifest> | null;
  if (!body || !Array.isArray(body.pages)) {
    console.warn('OCR manifest has no pages array; ignoring it.');
    return null;
  }
  if (
    typeof pdfPageCount === 'number' &&
    pdfPageCount > 0 &&
    body.pageCount !== pdfPageCount
  ) {
    console.warn(
      `Ignoring OCR manifest: it describes ${String(body.pageCount)} pages ` +
        `but the PDF has ${pdfPageCount}.`
    );
    return null;
  }
  return body as OcrManifest;
}

// ---------------------------------------------------------------------------
// Redaction-data asset (VE-20835)
//
// Save and load redactions to/from a `document-redaction-data` asset on the
// TDO, parallel to how video redactions live on the `redaction-media-data`
// asset. These services talk directly to the Veritone GraphQL API and are
// reachable from sagas via `yield* call(...)`.
// ---------------------------------------------------------------------------

interface CreateAssetResponse {
  createAsset: {
    id: string;
    createdDateTime: string;
    container: { id: string };
  };
}

interface ListRedactionAssetsResponse {
  temporalDataObject: {
    assets: {
      records: Array<{ id: string; createdDateTime: string }>;
    } | null;
  } | null;
}

interface LoadRedactionAssetResponse {
  temporalDataObject: {
    assets: {
      records: Array<{
        id: string;
        createdDateTime: string;
        signedUri: string;
        details?: Record<string, unknown>;
        container: { id: string };
      }>;
    } | null;
  } | null;
}

/**
 * Who wrote a `document-redaction-data` asset (VE-24948, review B4).
 *
 * Every save made in the editor leaves this off. The one writer that sets it is
 * the UPLOAD path: `recordUploadLexiconSelection` stamps the lexicons picked in
 * the upload dialog onto a document nobody has opened yet, which produces a
 * real asset for a document with no human work in it at all.
 *
 * That distinction is load-bearing rather than cosmetic. `openDocument` derives
 * `lastSavedAt` from the newest asset, and BOTH auto-apply sagas
 * (`lexiconAutoApply`, `watermarkAutoApply`) treat a non-null `lastSavedAt` as
 * "the reviewer has saved this document, so its own contents are the answer" —
 * the rule that lets someone remove an auto-applied lexicon or watermark and
 * have it stay removed. Counting the upload stub as a save turned that rule
 * against itself: picking a lexicon at upload silently suppressed auto-apply
 * for both features on the very first open.
 */
export type RedactionAssetWriter = 'upload';

/**
 * True when the asset was stamped by the upload pipeline rather than saved by a
 * person. Absent/unknown values read as false — an unmarked asset is a save,
 * which is the safe direction: the worst case is an auto-apply that does not
 * run, never one that overwrites work somebody did.
 */
export const isUploadWrittenRedactionAsset = (details: unknown): boolean =>
  (details as { writtenBy?: unknown } | null | undefined)?.writtenBy ===
  'upload';

/**
 * Save the redaction asset body as a new `document-redaction-data` asset
 * on the TDO via the Veritone GraphQL `createAsset` mutation. Mirrors the
 * multipart pattern used by saveMediaData for `redaction-media-data`.
 *
 * Body is uploaded as the multipart `file` form field; the mutation
 * references it via input.
 *
 * `expectedRedactedVersion` is set only by the save the export forces, and is
 * the version that burn-in is about to produce — see VE-26422. Passing it marks
 * the asset as sent for burn-in; leaving it out marks the asset as an ordinary
 * save that was not.
 *
 * `writtenBy` says a PIPELINE produced this asset rather than a person in the
 * editor — see `isUploadWrittenRedactionAsset`. Omitted for every editor save,
 * which is what makes an absent key mean "a human saved this".
 */
export async function serviceSaveDocumentRedactionData(
  tdoId: TDOId,
  body: DocumentRedactionAssetV1,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  expectedRedactedVersion?: number,
  writtenBy?: RedactionAssetWriter
): Promise<{ id: string; createdDateTime: string }> {
  const query = `
    mutation saveDocumentRedactionData($id: ID!, $details: JSONData!) {
      createAsset(input: {
        containerId: $id,
        type: "${DOCUMENT_REDACTION_ASSET_TYPE}",
        contentType: "application/json",
        details: $details
      }) {
        id
        createdDateTime
        container { id }
      }
    }
  `;
  // Asset-level metadata: lets loaders filter by format/metrics without
  // downloading the body. `assetFormatVersion` is also embedded in the
  // body for self-describing storage. `metrics.pagesRemoved` counts how
  // many pages the user hid from the output (VE-20838) — set
  // as a top-level details key too so consumers can read it without
  // descending into `metrics`.
  //
  // The metric KEYS keep their original names for downstream consumers even
  // though the UI vocabulary moved on: `pagesRemoved` counts today's "Hide
  // Page" and `pagesFullyRedacted` counts today's "Remove Page".
  const pagesRemoved = Object.values(body.redactions ?? {}).filter(
    (b) => b?.pageHidden === true || b?.pageRemoved === true
  ).length;
  const pagesFullyRedacted = Object.values(body.redactions ?? {}).filter(
    (b) => b?.pageContentRemoved === true || b?.pageFullyRedacted === true
  ).length;
  // VE-25870: how many redactions this save says to burn in, so a later reader
  // can tell whether a redacted output still matches the saved state without
  // downloading the body. PII matches are counted only when actually redacted —
  // an unredacted match is a suggestion sitting in the list, not an output
  // change. Assets written before this key simply have no count, which readers
  // treat as "unknown" rather than zero.
  const redactionCount = Object.values(body.redactions ?? {}).reduce(
    (total, bucket) =>
      total +
      Object.keys(bucket?.manual ?? {}).length +
      Object.keys(bucket?.keyword ?? {}).length +
      Object.keys(bucket?.['text-select'] ?? {}).length +
      (bucket?.PII ?? []).filter((match) => match?.redacted).length,
    0
  );
  const details = {
    assetFormatVersion: body.assetFormatVersion,
    pagesRemoved,
    pagesFullyRedacted,
    redactionCount,
    metrics: {
      pagesRemoved,
      pagesFullyRedacted,
    },
    // VE-26422: whether this save was sent for burn-in, and if so which output
    // version it was sent to produce. The staleness verdict then compares that
    // number against the newest redacted-document's `version` — both sides carry
    // it by agreement, so neither has to observe the other and the order the
    // assets land in stops mattering.
    //
    // Written on every save, including as `false`, because three states have to
    // stay distinguishable: sent for burn-in, saved but never sent (stale on its
    // own evidence), and the key being absent, which means the asset predates
    // this scheme and can only be judged by counts.
    burnInRequested: typeof expectedRedactedVersion === 'number',
    ...(typeof expectedRedactedVersion === 'number' && {
      expectedRedactedVersion,
    }),
    // VE-24948 (review B4): only present when a pipeline wrote this, so an
    // absent key reads as "an editor save" for every asset ever written,
    // including the ones that predate the key.
    ...(writtenBy && { writtenBy }),
  };

  const formData = new FormData();
  formData.append('query', query);
  formData.append('variables', JSON.stringify({ id: tdoId, details }));
  formData.append('filename', 'document-redactions.json');
  formData.append(
    'file',
    new Blob([JSON.stringify(body)], { type: 'application/json' }),
    'document-redactions.json'
  );

  const res = await fetch(`${apiRoot}/${graphQLEndpoint}`, {
    method: 'POST',
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  // Check the HTTP status before parsing — a non-2xx response (e.g. auth
  // proxy redirecting to a login page, gateway timeout, 500 with an HTML
  // error body) would otherwise throw a confusing JSON-parse error and
  // lose the actual status context.
  if (!res.ok) {
    throw new Error(`document-redaction-data save failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: CreateAssetResponse;
    errors?: Array<{ message?: string }>;
  };
  if (json.errors?.length) {
    const firstErr = json.errors[0]?.message ?? 'unknown error';
    throw new Error(`document-redaction-data save failed: ${firstErr}`);
  }
  const created = json.data?.createAsset;
  if (!created) {
    throw new Error('document-redaction-data save returned no asset');
  }
  return { id: created.id, createdDateTime: created.createdDateTime };
}

/**
 * Load the most-recent `document-redaction-data` asset for the TDO and
 * return its parsed body. Returns `null` when no asset exists yet.
 */
export async function serviceLoadDocumentRedactionData(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{
  body: DocumentRedactionAssetV1;
  createdDateTime: string;
  /** VE-26422: carries the burn-in label; see `readBurnInLabel`. */
  details?: Record<string, unknown>;
} | null> {
  const query = `
    query loadDocumentRedactionData($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${DOCUMENT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records {
            id
            createdDateTime
            signedUri
            # VE-26422: says whether this save was sent for burn-in, and for which
            # output version, so a reopened document knows without a second fetch.
            details
            container { id }
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<LoadRedactionAssetResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `document-redaction-data load failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  const record = result.data?.temporalDataObject?.assets?.records?.[0];
  if (!record) {
    return null;
  }
  // Validation: the asset's container must be the requested TDO. Same defensive
  // check that loadMediaDataAsset does for `redaction-media-data` — a mismatch
  // here means the asset metadata is corrupted (rare but seen in prod).
  if (record.container?.id && record.container.id !== tdoId) {
    throw new Error(
      'document-redaction-data load: container TDO mismatch (data corruption?)'
    );
  }
  const assetRes = await fetch(record.signedUri);
  if (!assetRes.ok) {
    throw new Error(
      `document-redaction-data fetch failed: HTTP ${assetRes.status}`
    );
  }
  const body = (await assetRes.json()) as DocumentRedactionAssetV1;
  if (body.tdoId && body.tdoId !== tdoId) {
    throw new Error(
      'document-redaction-data load: body tdoId mismatch (data corruption?)'
    );
  }
  return {
    body,
    createdDateTime: record.createdDateTime,
    details: record.details,
  };
}

// ---------------------------------------------------------------------------
// Watermark raster assets (VE-24693 §J). One asset per image watermark per
// document, holding the raw raster and nothing else. Written once and never
// rewritten, so a settings change costs a JSON save rather than an upload.
// ---------------------------------------------------------------------------

/**
 * Store one image watermark's raster as a `document-watermark-image` asset and
 * return its id, which the redaction-data body then references.
 *
 * Takes the base data URL the app already holds — the same 1024px PNG the
 * library definition carries — and uploads its DECODED bytes, not the base64.
 * Multipart `file`, exactly like the redaction-data save above.
 */
export async function serviceSaveWatermarkImageAsset(
  tdoId: TDOId,
  baseDataUrl: string,
  imageName: string | null,
  /**
   * Library watermark this raster came from. Stamped into the asset's `details`
   * so {@link serviceFindWatermarkImageAsset} can find it again — that lookup is
   * what makes applying a watermark idempotent, and it is the whole reason
   * nothing here has to be cleaned up. Null for an ad-hoc mark, which cannot be
   * matched and therefore uploads afresh.
   */
  watermarkId: string | null,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string
): Promise<{ id: string }> {
  const blob = watermarkDataUrlToBlob(baseDataUrl);
  const query = `
    mutation saveWatermarkImage($id: ID!, $details: JSONData!) {
      createAsset(input: {
        containerId: $id,
        type: "${DOCUMENT_WATERMARK_IMAGE_ASSET_TYPE}",
        contentType: "image/png",
        details: $details
      }) {
        id
        createdDateTime
        container { id }
      }
    }
  `;
  // `watermarkId` is the lookup key, not decoration: it lets a later apply of
  // the same watermark reuse this asset instead of storing the bytes again. The
  // mark's own attributes stay in redaction-data — that split is the point.
  const details = {
    imageName: imageName ?? undefined,
    watermarkId: watermarkId ?? undefined,
  };
  const formData = new FormData();
  formData.append('query', query);
  formData.append('variables', JSON.stringify({ id: tdoId, details }));
  formData.append('filename', 'watermark.png');
  formData.append('file', blob, 'watermark.png');

  const res = await fetch(`${apiRoot}/${graphQLEndpoint}`, {
    method: 'POST',
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    throw new Error(`document-watermark-image save failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: CreateAssetResponse;
    errors?: Array<{ message?: string }>;
  };
  if (json.errors?.length) {
    throw new Error(
      `document-watermark-image save failed: ${
        json.errors[0]?.message ?? 'unknown error'
      }`
    );
  }
  const created = json.data?.createAsset;
  if (!created) {
    throw new Error('document-watermark-image save returned no asset');
  }
  return { id: created.id };
}

/**
 * Decode a `data:image/png;base64,...` URL to a Blob for upload.
 *
 * Local to this module rather than reusing the uploader's helper: that one
 * accepts the three upload types, and what reaches here is only ever a PNG this
 * app rasterized. Anything else is a caller bug, and throwing says so instead
 * of posting whatever it was to the platform.
 */
function watermarkDataUrlToBlob(dataUrl: string): Blob {
  const match = /^data:image\/png;base64,([A-Za-z0-9+/]+={0,2})$/.exec(dataUrl);
  if (!match?.[1]) {
    throw new Error('Watermark image is not an inline PNG data URL');
  }
  const binary = atob(match[1]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'image/png' });
}

interface LoadWatermarkImageResponse {
  asset: {
    id: string;
    signedUri: string;
    container: { id: string } | null;
  } | null;
}

/**
 * Fetch one watermark raster back as a data URL, by asset id.
 *
 * A data URL rather than the signed URI itself, deliberately: the drawn form is
 * derived with `canvas.getImageData`, which TAINTS on a cross-origin image and
 * then throws. Reading the bytes here and re-inlining them keeps every
 * downstream consumer — overlay, derivation, burn-in — working on a same-origin
 * value, exactly as it did when the base was stored inline. The
 * `document-redaction-data` loader already fetches a signed URI this way, so no
 * new CORS surface is introduced.
 *
 * `tdoId` is checked against the asset's container for the same reason the
 * redaction-data loader does it: the id comes out of a body any org member can
 * write, and an asset from another TDO has no business rendering here.
 */
export async function serviceLoadWatermarkImageAsset(
  assetId: string,
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string | null> {
  const query = `
    query loadWatermarkImage($id: ID!) {
      asset(id: $id) {
        id
        signedUri
        container { id }
      }
    }
  `;
  const result = await fetchGraphQLApi<LoadWatermarkImageResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: assetId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `document-watermark-image load failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const record = result.data?.asset;
  if (!record?.signedUri) {
    return null;
  }
  // Fail CLOSED on a missing container. `container?.id &&` would wave through an
  // asset whose container came back null, which is the one shape a hand-written
  // id could produce.
  if (record.container?.id !== tdoId) {
    throw new Error(
      'document-watermark-image load: container TDO mismatch (data corruption?)'
    );
  }
  const res = await fetch(record.signedUri);
  if (!res.ok) {
    throw new Error(
      `document-watermark-image fetch failed: HTTP ${res.status}`
    );
  }
  const blob = await res.blob();
  // Vetted exactly as an inline raster is, and for the same reason: this asset
  // is org-writable, so its bytes are no more trustworthy than the body's were.
  //
  // Before 1.3.0 every raster reached a session through `watermarksFromAssetBody`
  // and therefore through this sanitizer, which checks a real PNG signature and
  // IHDR (not just the `data:image/png` prefix), the length cap, and the DECODED
  // PIXEL DIMENSIONS. Fetching from an asset added a second way in that skipped
  // all three — and `deriveWatermarkImage` could hand its input straight back
  // (it did so whenever grayscale was off and there was no tint, which is the
  // default for an image mark; today it still does when the white-removal pass
  // is abandoned), so the unvetted string landed in an `<img src>` and in the
  // burn-in worker's mupdf decoder untouched. Vetting HERE is what makes that
  // safe, and it must stay here: a re-encode downstream is an accident of what
  // the mark happens to need, not a guarantee.
  //
  // The pixel cap is the one that bites: a uniform 20000x20000 PNG is a few KB
  // on the wire and ~1.6GB decoded, so a single planted asset took out the tab
  // of everyone who opened the document. Returning null here puts a rejected
  // raster on the same path as a missing one — counted, reported, not drawn.
  return sanitizeStoredImageDataUrl(await blobToDataUrl(blob));
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // `readAsDataURL` yields a string, but the typed result is a union with
      // ArrayBuffer — assert nothing and reject rather than stringifying an
      // object into an `<img src>`.
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Watermark image did not read as a data URL'));
    };
    reader.onerror = () =>
      reject(new Error('Watermark image could not be read'));
    reader.readAsDataURL(blob);
  });
}

/**
 * How many labelled candidates are worth fetching before giving up and
 * uploading. Each costs a round trip, and the honest answer is almost always
 * the first: more than one only arises after a re-upload or when somebody has
 * planted assets, and in the planted case uploading afresh is the right outcome
 * anyway. Bounded so a TDO crowded with candidates cannot turn one apply into a
 * hundred fetches.
 */
const MAX_REUSE_CANDIDATES = 3;

interface ListWatermarkImageAssetsResponse {
  temporalDataObject: {
    assets: {
      records: Array<{
        id: string;
        details?: { watermarkId?: string | null } | null;
      }>;
    } | null;
  } | null;
}

/**
 * Find an existing `document-watermark-image` asset on this TDO for a given
 * library watermark, so applying the same watermark again reuses the raster
 * already stored rather than uploading it a second time.
 *
 * This lookup is what makes the whole scheme need no cleanup, which is worth
 * stating plainly because the alternative was tried on paper and is worse.
 * Uploading per apply means an asset abandoned every time a user adds a mark and
 * closes without saving; deleting on removal means a saved body can reference an
 * asset that a later removal deleted — a dangling `imageAssetId` and a mark with
 * no artwork. Reusing instead of creating removes both: an orphan is simply the
 * asset that the next apply of that watermark will find and use. The cost is one
 * asset per distinct watermark ever applied to a document, bounded by the size
 * of the library.
 *
 * A candidate is only adopted once its stored bytes have been fetched and found
 * identical to `expectedBaseDataUrl` — see the comment at the comparison for why
 * the label alone was not enough.
 *
 * Returns null for an ad-hoc mark (no library id to match on) or when nothing
 * verifies. A failure to LOOK is not a failure to apply — the caller uploads.
 */
export async function serviceFindWatermarkImageAsset(
  tdoId: TDOId,
  watermarkId: string | null,
  /** The raster about to be stored, i.e. what a reusable asset must contain. */
  expectedBaseDataUrl: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string | null> {
  if (!watermarkId || !expectedBaseDataUrl) {
    return null;
  }
  const query = `
    query findWatermarkImage($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${DOCUMENT_WATERMARK_IMAGE_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 100) {
          records {
            id
            details
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<ListWatermarkImageAssetsResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `document-watermark-image lookup failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const records = result.data?.temporalDataObject?.assets?.records ?? [];
  // `watermarkId` NARROWS the candidates; it does not identify a match. It is a
  // label in `details`, and `details` is writable by any org member through the
  // public API — so on its own it selected "an asset somebody claims is this
  // watermark", which is not the same thing as this watermark's artwork.
  //
  // Two ways that bit, one of them needing no attacker at all:
  //
  //  - A library watermark keeps its SDO id when its image is REPLACED, so a
  //    document holding an asset from the previous artwork reused the old
  //    raster. The export in that session showed the new image and the reopened
  //    document showed the old one — the two disagreeing about what was
  //    stamped, against the promise in `AppliedWatermark.imageAssetId`.
  //  - Any org member could pre-create an asset on the TDO carrying this label
  //    and arbitrary bytes. Ordering is newest-first, so the plant won. The
  //    victim applied the real watermark, the canvas drew the real artwork, and
  //    every later open and export shipped the planted one — a document the UI
  //    and the audit log both called watermarked.
  //
  // So the candidate's actual BYTES are compared against what is about to be
  // uploaded. A digest stamped into `details` would not do: it is written by
  // whoever wrote the asset, so it is the same self-assertion one level down.
  const candidates = records.filter(
    (r) => r.details?.watermarkId === watermarkId
  );
  for (const candidate of candidates.slice(0, MAX_REUSE_CANDIDATES)) {
    let stored: string | null = null;
    try {
      stored = await serviceLoadWatermarkImageAsset(
        candidate.id,
        tdoId,
        token,
        apiRoot,
        graphQLEndpoint,
        veritoneAppId
      );
    } catch {
      // Unreadable, wrong container, or rejected by the sanitizer. Not a
      // candidate, and not a reason to fail the apply — the caller uploads.
      continue;
    }
    if (stored && stored === expectedBaseDataUrl) {
      return candidate.id;
    }
  }
  // No verified match. The caller uploads, which is always correct and at worst
  // stores bytes the TDO already had.
  return null;
}

/**
 * Load the `document-redaction-data` asset that backs a *specific redacted
 * file* — the saved redaction state the burn-in actually consumed. Used by the
 * VE-22300 redaction report and by Side-by-Side View, which needs the pages
 * that output really dropped rather than the ones the editor has marked now.
 *
 * Two ways to identify it, tried in order:
 *
 * 1. `expectedVersion` — VE-26422's exact join. The forced pre-burn-in save
 *    stamps `details.expectedRedactedVersion` with the output version it was
 *    sent to produce, and the output stamps the same number as `details.version`,
 *    so the two sides match by agreement rather than by clock.
 * 2. Timestamp — the export saga forces that save immediately before creating
 *    the redacted-document asset, so the data tied to a redacted file is the
 *    newest one created at-or-before that file's timestamp. This is the only
 *    option for assets written before the labelling scheme existed.
 *
 * Falls back to the newest asset overall if neither lands (e.g. rotation
 * removed the exact match). Returns `null` when no redaction-data asset exists.
 */
export async function serviceLoadDocumentRedactionDataForRedactedFile(
  tdoId: TDOId,
  atOrBeforeIso: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string,
  expectedVersion?: number
): Promise<{
  body: DocumentRedactionAssetV1;
  createdDateTime: string;
} | null> {
  const query = `
    query loadDocumentRedactionDataForReport($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${DOCUMENT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 50) {
          records {
            id
            createdDateTime
            signedUri
            # VE-26422 burn-in label — lets us match the exact output version
            # instead of inferring it from timestamps.
            details
            container { id }
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<LoadRedactionAssetResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `document-redaction-data load failed: ${result.errors[0].message ?? 'unknown error'}`
    );
  }
  const records = result.data?.temporalDataObject?.assets?.records ?? [];
  if (records.length === 0) {
    return null;
  }
  // Records are newest-first. Prefer the asset that names this exact output
  // version; then the newest at-or-before the redacted file; then the newest
  // overall.
  const cutoff = new Date(atOrBeforeIso).getTime();
  const byVersion =
    typeof expectedVersion === 'number'
      ? records.find((r) => {
          const label = readBurnInLabel(r.details);
          return (
            label.state === 'burnedIn' && label.version === expectedVersion
          );
        })
      : undefined;
  const record =
    byVersion ??
    (Number.isFinite(cutoff)
      ? records.find((r) => new Date(r.createdDateTime).getTime() <= cutoff)
      : undefined) ??
    records[0];
  if (!record) {
    return null;
  }
  if (record.container?.id && record.container.id !== tdoId) {
    throw new Error(
      'document-redaction-data load: container TDO mismatch (data corruption?)'
    );
  }
  const assetRes = await fetch(record.signedUri);
  if (!assetRes.ok) {
    throw new Error(
      `document-redaction-data fetch failed: HTTP ${assetRes.status}`
    );
  }
  const body = (await assetRes.json()) as DocumentRedactionAssetV1;
  if (body.tdoId && body.tdoId !== tdoId) {
    throw new Error(
      'document-redaction-data load: body tdoId mismatch (data corruption?)'
    );
  }
  return { body, createdDateTime: record.createdDateTime };
}

/**
 * List ids of all `document-redaction-data` assets on the TDO, newest first.
 * Used by the asset-rotation step (keep N most recent, delete the rest).
 */
// Per-request cap for the assets query (the GraphQL `limit`). The function
// pages through with `offset` until every asset has been collected, so this is
// only the request granularity, not an overall ceiling.
const DOCUMENT_REDACTION_ASSET_PAGE_SIZE = 500;

export async function serviceListDocumentRedactionAssets(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<Array<{ id: string; createdDateTime: string }>> {
  const query = `
    query listDocumentRedactionAssets($id: ID!, $limit: Int!, $offset: Int!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${DOCUMENT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: $limit, offset: $offset) {
          records {
            id
            createdDateTime
          }
        }
      }
    }
  `;

  const records: Array<{ id: string; createdDateTime: string }> = [];
  for (let offset = 0; ; offset += DOCUMENT_REDACTION_ASSET_PAGE_SIZE) {
    const result = await fetchGraphQLApi<ListRedactionAssetsResponse>({
      endpoint: `${apiRoot}/${graphQLEndpoint}`,
      query,
      variables: {
        id: tdoId,
        limit: DOCUMENT_REDACTION_ASSET_PAGE_SIZE,
        offset,
      },
      token,
      veritoneAppId,
    });
    if (result.errors?.length) {
      throw new Error(
        `document-redaction-data list failed: ${result.errors[0].message ?? 'unknown error'}`
      );
    }
    const page = result.data?.temporalDataObject?.assets?.records ?? [];
    records.push(...page);
    // A short page (fewer than requested) means we've reached the end.
    if (page.length < DOCUMENT_REDACTION_ASSET_PAGE_SIZE) {
      break;
    }
  }
  return records;
}

/**
 * Best-effort delete of a single asset by id. Mirrors `deleteAssetById`
 * from `common-app/mediaDetails/services.ts` but is callable from sagas
 * without a thunk. Failures are logged and swallowed so the asset
 * rotation step can iterate through multiple ids without one bad id
 * aborting the rest.
 */
/**
 * Persist a `details` update to a specific asset. Used by the download
 * counter to write the bumped count back to the asset (mirrors video's
 * updateLastRedactedFileMetrics).
 */
export async function serviceUpdateAssetDetails(
  assetId: string,
  details: RedactedDocumentDetails,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const query = `
    mutation updateAssetDetails($id: ID!, $details: JSONData!) {
      updateAsset(input: { id: $id, details: $details }) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<{ updateAsset: { id: string } }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: assetId, details },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    const firstErr = String(result.errors[0]?.message ?? 'unknown error');
    throw new Error(`asset details update failed: ${firstErr}`);
  }
}

/**
 * Deletes a single asset by id. Throws on both GraphQL-level and transport
 * failures so callers can distinguish success from failure (the rotation
 * sagas wrap this in try/catch to make non-fatal cleanup; user-initiated
 * deletes propagate the failure into a snackbar).
 */
export async function serviceDeleteAsset(
  assetId: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const query = `
    mutation deleteAsset($id: ID!) {
      deleteAsset(id: $id) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<{ deleteAsset: { id: string } }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: assetId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    const firstErr = String(result.errors[0]?.message ?? 'unknown error');
    throw new Error(`deleteAsset failed for ${assetId}: ${firstErr}`);
  }
}

// ---------------------------------------------------------------------------
// Redacted-document asset (VE-20837 burn-in)
//
// The output of the burn-in flow: a brand-new `redacted-document` asset on
// the TDO whose body is the PDF with redactions baked in (text removed via
// mupdf.applyRedactions). Sister API to the video flow's `redaction-media`
// asset.
// ---------------------------------------------------------------------------

/**
 * Per-origin redaction counts + lifecycle counters persisted on the asset
 * so a reopen can recover them without re-rendering the PDF. Today only
 * `manual` (set on save) and `downloads` (incremented on each download) are
 * written; `textSelect` is reserved for the upcoming text-selection
 * redaction flow.
 */
export interface RedactedDocumentMetrics {
  manual?: { count: number };
  /**
   * Text-selection redaction metrics. Older assets wrote `{ count }`; current
   * assets write `{ rowCount, wordCount, uniqueCount }`. Readers tolerate both.
   */
  textSelect?: TextSelectMetrics | { count: number };
  /** Keyword-match redaction metrics. Absent on assets burned in before this. */
  keywords?: KeywordMetrics;
  /** PII-match redaction metrics. Absent on assets burned in before this. */
  PII?: PiiMetrics;
  /** Cumulative download count across all clients. */
  downloads?: number;
  /** Page-removal count (VE-20838). Mirrors top-level `details.pagesRemoved`. */
  pagesRemoved?: number;
  /**
   * Full-page-redaction count. Mirrors top-level `details.pagesFullyRedacted`.
   */
  pagesFullyRedacted?: number;
}

export interface RedactedDocumentDetails {
  /**
   * Monotonic version number for this redacted output. Bumped on each
   * burn-in; mirrored on `tdo.details.latestRedactedDocumentVersion` so a
   * resumed session can compute the next version even after older redacted
   * assets have been rotated away.
   */
  version: number;
  /** Byte length of the redacted PDF. Mirrors the asset-level size field. */
  assetSize: number;
  /** Page count of the redacted PDF. */
  numPages: number;
  /** First page included in the redacted output (1-indexed). */
  startPage: number;
  /** Last page included in the redacted output (1-indexed). */
  endPage: number;
  /**
   * WHICH FILE THIS WAS CUT FROM — the id of the `media` asset whose bytes the
   * burn-in consumed.
   *
   * A document TDO holds more than one `media` asset once OCR has run (the
   * ingested file and OCR's searchable copy), and the export deliberately
   * prefers the un-enriched one. Without this recorded, answering "which of
   * them did we burn?" for a released document meant reasoning backwards from
   * timestamps and engine ids — it took an hour once, which is why it is here.
   *
   * Null on assets written before this existed, and on an export whose source
   * could not be identified.
   */
  sourceMediaAssetId?: string | null;
  /**
   * Whether the burn-in fell back to the file the session had open because no
   * asset could be SHOWN to be the un-enriched original.
   *
   * True is the outcome the source resolution exists to avoid: the released
   * document may carry OCR's inferred text layer. It is recorded rather than
   * only warned about because the reviewer who needs to know is usually not the
   * one who was watching when it happened — a blank `documentOcrEngineId` (the
   * documented rollback lever) makes every enriched document take this path.
   */
  sourceIsEnrichedFallback?: boolean;
  /** Per-origin redaction counts so the UI can show them on a later open. */
  metrics?: RedactedDocumentMetrics;
}

interface CreateRedactedDocumentAssetResponse {
  createAsset: {
    id: string;
    createdDateTime: string;
    name: string | null;
    signedUri: string | null;
    container: { id: string };
  };
}

interface ListRedactedDocumentAssetsResponse {
  temporalDataObject: {
    assets: {
      records: Array<{
        id: string;
        name: string | null;
        createdDateTime: string;
        assetSize: number | null;
        signedUri: string | null;
        details: RedactedDocumentDetails | null;
      }>;
    } | null;
  } | null;
}

interface TdoDetailsResponse {
  temporalDataObject: {
    details: {
      latestRedactedDocumentVersion?: number;
      [k: string]: unknown;
    } | null;
  } | null;
}

interface UpdateTdoResponse {
  updateTDO: { id: string };
}

/**
 * Save the burn-in result as a new `redacted-document` asset on the TDO.
 * Mirrors the multipart createAsset pattern used by saveMediaData. The PDF
 * is uploaded as the multipart `file` form field; the asset body holds the
 * binary PDF (not JSON).
 */
export async function serviceSaveRedactedDocumentAsset(
  tdoId: TDOId,
  pdfBytes: Uint8Array,
  filename: string,
  details: RedactedDocumentDetails,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{
  id: string;
  createdDateTime: string;
  name: string | null;
  signedUri: string | null;
}> {
  // The server infers asset size from the uploaded file; `assetSize` lives
  // inside `details` instead. Mirrors how saveRedactedImageAsset wires the
  // createAsset mutation today.
  const query = `
    mutation saveRedactedDocument(
      $id: ID!,
      $name: String!,
      $details: JSONData!
    ) {
      createAsset(input: {
        containerId: $id,
        type: "redacted-document",
        contentType: "application/pdf",
        name: $name,
        details: $details
      }) {
        id
        createdDateTime
        name
        signedUri
        container { id }
      }
    }
  `;

  const formData = new FormData();
  formData.append('query', query);
  formData.append(
    'variables',
    JSON.stringify({ id: tdoId, name: filename, details })
  );
  formData.append('filename', filename);
  formData.append(
    'file',
    new Blob([pdfBytes as BlobPart], { type: 'application/pdf' }),
    filename
  );

  // Match the headers other GraphQL paths send (callApi/saveAsset/etc.):
  // `Authorization` + `x-veritone-application`. The GraphQL gateway
  // enforces `x-veritone-application` for asset uploads in some envs;
  // without it the multipart createAsset returns a 400.
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (veritoneAppId) {
    headers['x-veritone-application'] = veritoneAppId;
  }
  const res = await fetch(`${apiRoot}/${graphQLEndpoint}`, {
    method: 'POST',
    body: formData,
    headers,
  });
  if (!res.ok) {
    throw new Error(`redacted-document save failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: CreateRedactedDocumentAssetResponse;
    errors?: Array<{ message?: string }>;
  };
  if (json.errors?.length) {
    const firstErr = json.errors[0]?.message ?? 'unknown error';
    throw new Error(`redacted-document save failed: ${firstErr}`);
  }
  const created = json.data?.createAsset;
  if (!created) {
    throw new Error('redacted-document save returned no asset');
  }
  return {
    id: created.id,
    createdDateTime: created.createdDateTime,
    name: created.name,
    signedUri: created.signedUri,
  };
}

/**
 * List every `redacted-document` asset on the TDO, newest first. Returns
 * each record's `details.version` so callers can compute `max + 1` for the
 * next save and identify older assets for cleanup.
 */
export async function serviceListRedactedDocumentAssets(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<
  Array<{
    id: string;
    name: string | null;
    createdDateTime: string;
    assetSize: number | null;
    signedUri: string | null;
    details: RedactedDocumentDetails | null;
  }>
> {
  const query = `
    query listRedactedDocumentAssets($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "redacted-document", orderBy: createdDateTime, orderDirection: desc, limit: 500) {
          records {
            id
            name
            createdDateTime
            assetSize
            signedUri
            details
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<ListRedactedDocumentAssetsResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    const firstErr = String(result.errors[0]?.message ?? 'unknown error');
    throw new Error(`redacted-document list failed: ${firstErr}`);
  }
  return result.data?.temporalDataObject?.assets?.records ?? [];
}

export interface AuditLogAssetRecord {
  id: string;
  signedUri: string | null;
  details: { redactedMediaAssetId?: string; version?: number } | null;
}

/**
 * List the TDO's `redact-audit-log` CSV assets (VE-20840). Each carries a
 * `details.redactedMediaAssetId` linking it to a redacted-document asset, so
 * the reopen path can re-attach `auditLogUrl` to the matching version. Mirrors
 * `serviceListRedactedDocumentAssets`.
 */
export async function serviceListAuditLogAssets(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<AuditLogAssetRecord[]> {
  const query = `
    query listAuditLogAssets($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "redact-audit-log", orderBy: createdDateTime, orderDirection: desc, limit: 500) {
          records {
            id
            signedUri
            details
          }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject?: {
      assets?: { records?: AuditLogAssetRecord[] };
    };
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    const firstErr = String(result.errors[0]?.message ?? 'unknown error');
    throw new Error(`audit-log list failed: ${firstErr}`);
  }
  return result.data?.temporalDataObject?.assets?.records ?? [];
}

/**
 * Read just the TDO's `details.latestRedactedDocumentVersion` field. Used as
 * the version-source fallback when every prior redacted-document asset has
 * already been rotated away (so we can't recover the version from assets).
 */
export async function serviceFetchTdoLatestRedactedDocumentVersion(
  tdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<number | null> {
  const query = `
    query tdoLatestRedactedDocumentVersion($id: ID!) {
      temporalDataObject(id: $id) {
        details
      }
    }
  `;
  const result = await fetchGraphQLApi<TdoDetailsResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    const firstErr = String(result.errors[0]?.message ?? 'unknown error');
    throw new Error(`tdo details fetch failed: ${firstErr}`);
  }
  const v =
    result.data?.temporalDataObject?.details?.latestRedactedDocumentVersion;
  return typeof v === 'number' ? v : null;
}

/**
 * Persist the latest version on the TDO so future saves can resume numbering
 * even if every prior `redacted-document` asset has been deleted. Mirrors
 * imageRedactionSaga's `updateLatestRedactedVersionOnDetails` for the image
 * flow (which writes `latestRedactedMediaVersion`).
 */
export async function serviceUpdateTdoLatestRedactedDocumentVersion(
  tdoId: TDOId,
  version: number,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const query = `
    mutation setLatestRedactedDocumentVersion($id: ID!, $details: JSONData!) {
      updateTDO(input: { id: $id, details: $details }) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<UpdateTdoResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: {
      id: tdoId,
      details: { latestRedactedDocumentVersion: version },
    },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    const firstErr = String(result.errors[0]?.message ?? 'unknown error');
    throw new Error(`tdo version update failed: ${firstErr}`);
  }
}

/**
 * Persist the document editor's filename onto the TDO. Mirrors common-app's
 * `updateNameOnDetails` so the new name lands in the same three places the
 * video flow writes: the TDO's top-level `name`, `details.name`, and
 * `details.veritoneFile.{fileName, filename}`. The case duplication is the
 * existing convention — different downstream consumers read different
 * fields, so writing only `name` leaves stale values behind that surface
 * the moment the page reloads.
 *
 * We fetch the TDO's current details first so we can preserve unrelated
 * fields (audits, tags, statuses, etc.) while merging in just the renamed
 * pieces. `formatTDOName` matches the video flow's input sanitization.
 */
export async function serviceUpdateTdoName(
  tdoId: TDOId,
  name: string,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<string> {
  const formatted = formatTDOName(name);
  const readQuery = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        details
      }
    }
  `;
  const readResult = await fetchGraphQLApi<{
    temporalDataObject: { details: Record<string, unknown> | null } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query: readQuery,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (readResult.errors?.length) {
    const firstErr = String(readResult.errors[0]?.message ?? 'unknown error');
    throw new Error(`tdo details fetch failed: ${firstErr}`);
  }
  const details = (readResult.data?.temporalDataObject?.details ??
    {}) as Record<string, unknown> & {
    veritoneFile?: Record<string, unknown> | null;
  };

  const mutation = `
    mutation updateNameTDO($id: ID!, $name: String!, $details: JSONData) {
      updateTDO(input: { id: $id, name: $name, details: $details }) {
        id
        name
        modifiedDateTime
      }
    }
  `;
  const writeResult = await fetchGraphQLApi<UpdateTdoResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query: mutation,
    variables: {
      id: tdoId,
      name: formatted,
      details: {
        ...details,
        name: formatted,
        veritoneFile: {
          ...(details.veritoneFile ?? {}),
          fileName: formatted,
          filename: formatted,
        },
      },
    },
    token,
    veritoneAppId,
  });
  if (writeResult.errors?.length) {
    const firstErr = String(writeResult.errors[0]?.message ?? 'unknown error');
    throw new Error(`tdo rename failed: ${firstErr}`);
  }
  return formatted;
}

/**
 * Persist the document editor's redaction status onto the TDO's
 * `details.tags` (VE-21914). Mirrors the video flow's
 * `updateRedactionStatusOnDetails` from common-app/mediaDetails: read the
 * existing tags, merge or append the `IN_REDACTION_TAG_KEY` entry, and write
 * back via `updateTDO`. The doc editor doesn't share the `mediaDetails`
 * slice, so we own a parallel write path that targets the same TDO field.
 *
 * Done in one round-trip so the saga can stay simple — we re-fetch tags
 * here instead of trusting a possibly-stale snapshot from open-time
 * (status changes are infrequent and tags are tiny, so the extra read is
 * cheap compared to risking a clobber of unrelated tags).
 */
export async function serviceUpdateTdoRedactionStatus(
  tdoId: TDOId,
  fields: RedactionStatusTagWriteFields,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  const readQuery = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        details
      }
    }
  `;
  const readResult = await fetchGraphQLApi<{
    temporalDataObject: { details: Record<string, unknown> | null } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query: readQuery,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (readResult.errors?.length) {
    const firstErr = String(readResult.errors[0]?.message ?? 'unknown error');
    throw new Error(`tdo details fetch failed: ${firstErr}`);
  }
  const details = readResult.data?.temporalDataObject?.details ?? {};
  const originalTags = Array.isArray(
    (details as { tags?: unknown }).tags as TdoTagRecord[] | undefined
  )
    ? ((details as { tags?: TdoTagRecord[] }).tags ?? [])
    : [];
  const tagIndex = originalTags.findIndex(
    (tag) => tag?.value === IN_REDACTION_TAG_KEY
  );
  const detailTags: TdoTagRecord[] =
    tagIndex > -1
      ? originalTags.map((tag, idx) =>
          idx === tagIndex
            ? mergeRedactionStatusTagFields(
                { ...tag, value: IN_REDACTION_TAG_KEY },
                fields
              )
            : tag
        )
      : [
          ...originalTags,
          mergeRedactionStatusTagFields(
            { value: IN_REDACTION_TAG_KEY },
            fields
          ),
        ];

  const mutation = `
    mutation setRedactionStatus($id: ID!, $details: JSONData!) {
      updateTDO(input: { id: $id, details: $details }) {
        id
      }
    }
  `;
  const writeResult = await fetchGraphQLApi<UpdateTdoResponse>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query: mutation,
    variables: {
      id: tdoId,
      details: { ...details, tags: detailTags },
    },
    token,
    veritoneAppId,
  });
  if (writeResult.errors?.length) {
    const firstErr = String(writeResult.errors[0]?.message ?? 'unknown error');
    throw new Error(`tdo status update failed: ${firstErr}`);
  }
}

// ---------------------------------------------------------------------------
// GraphQL service: audit event emission
// Fire-and-forget Redux Thunk dispatched with `yield* put(serviceEmitAuditEvent(...))`
// ---------------------------------------------------------------------------

export const EMIT_AUDIT_INIT = 'redact/documents/EMIT_AUDIT_INIT';
export const EMIT_AUDIT_SUCCESS = 'redact/documents/EMIT_AUDIT_SUCCESS';
export const EMIT_AUDIT_FAILURE = 'redact/documents/EMIT_AUDIT_FAILURE';

/**
 * The document editor's audit emitter, now a thin call onto the shared
 * `emitTdoAuditEvent` (`@helpers/emitTdoAuditEvent`) — lifted there when the
 * folder view needed the same thing and mediaDetails' emitter could not serve
 * it (that one reads its tdoId from the OPEN editor file). The dispatch
 * breadcrumbs stay this module's own so nothing downstream of them changes.
 *
 * The `redact` application bucket is not negotiable and lives in the helper:
 * the platform only makes events queryable by `terms: [{ tdoId }]` for an
 * application with a server-side audit schema indexing tdoId, and `redact` is
 * the one that has it. An ad-hoc name emits fine and reads back never — which
 * is exactly why the generated CSV once came back empty.
 */
export const serviceEmitAuditEvent = (
  tdoId: TDOId,
  action: string,
  payload?: Record<string, unknown>
): ThunkAction<Promise<unknown>, unknown, unknown, UnknownAction> =>
  emitTdoAuditEvent(String(tdoId), action, payload, [
    EMIT_AUDIT_INIT,
    EMIT_AUDIT_SUCCESS,
    EMIT_AUDIT_FAILURE,
  ]);

// ---------------------------------------------------------------------------
// Audit-log CSV asset (VE-20840)
//
// On burn-in, gather every `redact` audit event for the TDO and write
// them out as a `redact-audit-log` CSV asset — the document-mode counterpart
// of `mediaDetails`' `uploadImageAuditLog`. The CSV format
// (`formatAuditEventsCsv`) and asset upload (`saveAuditLogAsset`) are shared
// with the image path; only the `application` filter differs. The asset's
// `details.redactedMediaAssetId` links it back to the redacted-document asset
// so the Redacted File tab can resolve the matching audit log on reopen.
// ---------------------------------------------------------------------------

export interface CreatedDocumentAuditLogAsset {
  id: string;
  name: string;
  contentType: string;
  signedUri: string;
  details: Record<string, unknown>;
}

export async function uploadDocumentAuditLog(
  tdoId: string,
  redactedDocumentAssetId: string,
  version: number,
  redactedDocumentFileName: string,
  getState: GQLApiGetState
): Promise<CreatedDocumentAuditLogAsset | null> {
  try {
    const auditEventsQuery = `
      query auditEvents($id: ID!, $application: String, $offset: Int, $limit: Int) {
        auditEvents(application: $application, terms: [{ tdoId: $id }], offset: $offset, limit: $limit) {
          count
          records {
            id
            payload
            application
          }
        }
      }
    `;

    const limit = 100;
    let offset = 0;
    const allRecords: AuditEventRecord[] = [];
    for (;;) {
      const resp = await callGraphQLApi<{
        auditEvents: { count: number; records: AuditEventRecord[] };
      }>({
        actionTypes: ['NOOP', 'NOOP', 'NOOP'],
        query: auditEventsQuery,
        variables: {
          id: tdoId,
          application: AUDIT_APPLICATION,
          offset,
          limit,
        },
        // callGraphQLApi dispatches unconditionally; NOOP action types make
        // those dispatches side-effect-free.
        dispatch: ((_action: unknown) => {}) as GQLApiDispatch,
        getState,
        throwOnError: true,
      });

      const records = resp?.auditEvents?.records ?? [];
      allRecords.push(...records);
      if (records.length < limit) {
        break;
      }
      offset += limit;
    }

    // MuPDF license-meter events are Veritone's record, not the customer's
    // chain of custody — keep them out of the CSV (see ./licenseMeter.ts).
    const csv = formatAuditEventsCsv(withoutLicenseMeterRecords(allRecords));

    const result: any = await saveAuditLogAsset(
      {
        tdoId,
        name: `${redactedDocumentFileName}.csv`,
        redactedMediaAssetId: redactedDocumentAssetId,
        version,
      },
      csv,
      getState
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const created = result?.data?.createAsset;
    if (!created?.id) {
      return null;
    }
    return created as CreatedDocumentAuditLogAsset;
  } catch (error) {
    console.error(
      '[uploadDocumentAuditLog] Failed to create audit log asset:',
      error
    );
    return null;
  }
}

// ---------------------------------------------------------------------------
// Document comments (VE-21982)
//
// Parallels `fetchMediaComments` / `updateComment` from
// `@common-modules/mediaDetails` but targets the redact-document-comment
// registry. The same GraphQL surface (`structuredDataObjects`,
// `createStructuredData`) is used — only the schemaId differs.
// ---------------------------------------------------------------------------

export interface FetchDocumentCommentsResponse {
  structuredDataObjects: {
    offset: number;
    records: Array<{ data: DocumentComment }>;
  };
}

export interface UpdateDocumentCommentResponse {
  createStructuredData: { data: DocumentComment };
  /** Threaded through the dispatch wrapper so the reducer can skip undo recording. */
  isUndo?: boolean;
}

type DispatchFor<T> = GQLApiDispatch<T>;

export const fetchDocumentComments =
  ({ tdoId, limit, offset }: { tdoId: TDOId; limit: number; offset: number }) =>
  async (
    dispatch: DispatchFor<FetchDocumentCommentsResponse>,
    getState: GQLApiGetState
  ) => {
    const schemaId = await lookupLatestDocumentCommentsSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }

    const query = `
      query fetchDocumentComments($schemaId: ID!, $limit: Int!, $offset: Int!, $tdoId: ID!) {
        structuredDataObjects(
          limit: $limit
          offset: $offset
          schemaId: $schemaId
          filter: {
            tdoId: $tdoId
          }
        ) {
          offset
          records {
            data
          }
        }
      }
    `;

    return await callGraphQLApi<FetchDocumentCommentsResponse>({
      actionTypes: [
        '',
        FETCH_DOCUMENT_COMMENTS_SUCCESS,
        FETCH_DOCUMENT_COMMENTS_FAILURE,
      ],
      query,
      variables: { schemaId, limit, offset, tdoId },
      dispatch,
      getState,
    });
  };

export const updateDocumentComment =
  ({
    comment,
    modify = true,
    isUndo,
  }: GetActionCreatorPayloadT<typeof UPDATE_DOCUMENT_COMMENT>) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const currentTime = new Date().toISOString();
    const { commentId: sdoId, createdDateTime } = comment;
    const schemaId = await lookupLatestDocumentCommentsSchemaId(
      dispatch,
      getState
    );

    if (!schemaId) {
      return;
    }

    const updatedComment: DocumentComment = {
      ...comment,
      createdDateTime: createdDateTime || currentTime,
      modifiedDateTime:
        modify && !isUndo ? currentTime : comment.modifiedDateTime,
      done: true,
    };

    const query = `
      mutation upsertDocumentComment($schemaId: ID!, $sdoId: ID!, $comment: JSONData!) {
        createStructuredData(input: {
          id: $sdoId
          schemaId: $schemaId
          data: $comment
        }) {
          data
        }
      }
    `;

    return await callGraphQLApi<{
      createStructuredData: { data: DocumentComment };
    }>({
      actionTypes: [
        '',
        UPDATE_DOCUMENT_COMMENT_SUCCESS,
        UPDATE_DOCUMENT_COMMENT_FAILURE,
      ],
      query,
      variables: { schemaId, sdoId, comment: updatedComment },
      // Mirrors the media-comments thunk: thread `isUndo` onto the dispatched
      // success/failure action payload so the reducer can skip pushing an
      // undo entry when we're already applying one.
      dispatch: (a) => dispatch({ ...a, payload: { ...a.payload, isUndo } }),
      getState,
    });
  };

// ---------------------------------------------------------------------------
// Background TDO locks (VE-29142)
// ---------------------------------------------------------------------------

/**
 * Take or refresh THIS user's lock record on a document that is NOT on screen.
 *
 * Why not `checkTdoLock`, which every other lock caller uses: that action
 * resolves through `watchTdoLockFound` / `watchTdoLockNotFound`
 * (`common-app/state/modules/mediaDetails/saga/view.ts`), and neither handler
 * reads the tdoId off the response. Both write the single global
 * `tdoLockInfo`, which `user-permissions` turns into `viewOnlyPermissions` and
 * the editor reads as `canEdit`. That was safe while the only caller was the
 * document on screen. Firing it for a BACKGROUND document made a per-document
 * fact overwrite a single-tenant slot: a colleague's lock on a parked file
 * could flip the on-screen file to view-only naming the wrong document, and —
 * the dangerous direction — a parked file whose lock is ours could clear a
 * GENUINE lock on the file being edited, handing two reviewers the same
 * document and losing one of their redaction sets on the next save.
 *
 * So this deliberately does LESS. It never asks who holds the lock and never
 * touches `tdoLockInfo`; it only writes our own record. Evaluating ownership
 * is the on-screen document's job, and `DocumentDetails` already does it on
 * the way in — so a document the reviewer switches back to is assessed
 * properly at that point.
 *
 * Records are per user (`removeTdoLock` filters by `{tdoId, userId}`), so
 * "we hold a lock" is exactly "our record exists and is fresh". The existing
 * record's id is reused when there is one, which is what keeps a 60-second
 * heartbeat from minting a new SDO per tick.
 */
export const serviceRefreshBackgroundTdoLock =
  ({
    tdoId,
    userId,
    name,
  }: {
    tdoId: string;
    userId: string;
    name: string;
  }): ThunkAction<Promise<void>, unknown, unknown, UnknownAction> =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }

    // Filtered by userId as well as tdoId: we want OUR record, not a verdict
    // on whether anyone else holds one.
    const existing = await callGraphQLApi<{
      structuredDataObjects: { records: { id: string }[] };
    }>({
      actionTypes: ['', '', ''],
      query: `
      query findOwnTdoLock($schemaId: ID!, $tdoId: ID!, $userId: ID!) {
        structuredDataObjects(
          schemaId: $schemaId
          filter: { tdoId: $tdoId, userId: $userId }
          orderBy: { field: modifiedDateTime, direction: asc }
        ) {
          records { id }
        }
      }`,
      variables: { schemaId, tdoId, userId },
      dispatch,
      getState,
    });

    const id = existing?.structuredDataObjects?.records?.[0]?.id;

    // `createStructuredData` with an id updates that record in place; without
    // one it creates. Same mutation either way, which is what lets acquire and
    // refresh share this path.
    await callGraphQLApi({
      actionTypes: ['', '', ''],
      query: `
      mutation secureBackgroundTdoLock($schemaId: ID!, $id: ID, $name: ID!, $tdoId: ID!, $userId: ID!, $lastAccessed: String!) {
        createStructuredData(
          input: {
            id: $id
            schemaId: $schemaId
            data: { name: $name, tdoId: $tdoId, userId: $userId, lastAccessed: $lastAccessed }
          }
        ) {
          id
        }
      }`,
      variables: {
        schemaId,
        id,
        name,
        tdoId,
        userId,
        lastAccessed: new Date().toISOString(),
      },
      dispatch,
      getState,
    });
  };
